import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Modal, Typography } from '@mui/material';

import WaeButton, { BUTTON_TYPE, BUTTON_VARIANT } from '../../../../../../../components/Button';
import DatePickerField from '../../../../../../../components/DatePicker/DatePickerField';
import { BLACK, PRIMARY_PURPLE } from '../../../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../../../theme/fontConstants';
import { formatDateOnlyString, parseDateOnlyString } from '../../../../../../../utils';

import {
  getValidityRangeOrderingErrorKeys,
  isExpirationSaveDisabled,
} from './DocumentExpirationModal.helpers';

const modalRootSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '420px' },
  bgcolor: BLACK[0],
  borderRadius: '16px',
  boxShadow: 24,
  p: '30px 38px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const titleSx = {
  fontSize: '20px',
  fontFamily: PRIMARY_FONT[700],
  color: BLACK[100],
  lineHeight: '24px',
};

const fieldLabelSx = {
  fontFamily: PRIMARY_FONT[500],
  fontSize: '14px',
  color: BLACK[100],
  mb: 0.75,
};

const fieldsSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const footerSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 1.5,
  mt: 1,
};

const pillButtonSx = {
  borderRadius: '100px',
  fontFamily: PRIMARY_FONT[500],
  fontSize: '14px',
  px: 3,
  py: 1,
  boxShadow: 'none',
  minHeight: '36px',
  height: 'auto',
};

const cancelButtonSx = {
  ...pillButtonSx,
  color: BLACK[100],
  border: `1px solid ${BLACK[30]}`,
  '&:hover': {
    backgroundColor: BLACK[10],
    boxShadow: 'none',
    border: `1px solid ${BLACK[30]}`,
  },
};

const saveButtonSx = {
  ...pillButtonSx,
  color: BLACK[0],
  '&:hover': {
    backgroundColor: PRIMARY_PURPLE[60],
    boxShadow: 'none',
  },
};

const DATE_PICKER_OVERLAY_PROPS = {
  sx: { zIndex: 1500 },
};

const EXPIRATION_FIELDS = [
  {
    key: 'validFrom',
    labelKey: 'userDetail.employeeDocumentation.expiration.validFrom',
    testId: 'expiration-valid-from',
  },
  {
    key: 'validNoticeFrom',
    labelKey: 'userDetail.employeeDocumentation.expiration.softExpiration',
    testId: 'expiration-soft',
  },
  {
    key: 'validThrough',
    labelKey: 'userDetail.employeeDocumentation.expiration.hardExpiration',
    testId: 'expiration-hard',
  },
];

const DocumentExpirationModal = ({ open, document, isSaving, onCancel, onSave }) => {
  const [validFrom, setValidFrom] = useState(null);
  const [validNoticeFrom, setValidNoticeFrom] = useState(null);
  const [validThrough, setValidThrough] = useState(null);

  useEffect(() => {
    if (!open) return;

    setValidFrom(parseDateOnlyString(document?.validity?.range?.validFrom));
    setValidNoticeFrom(parseDateOnlyString(document?.validity?.range?.validNoticeFrom));
    setValidThrough(parseDateOnlyString(document?.validity?.range?.validThrough));
  }, [open, document]);

  const rangeDates = { validFrom, validNoticeFrom, validThrough };
  const orderingErrorKeys = getValidityRangeOrderingErrorKeys(rangeDates);
  const isSaveDisabled = isExpirationSaveDisabled({ ...rangeDates, isSaving });

  const fieldValues = {
    validFrom,
    validNoticeFrom,
    validThrough,
  };

  const fieldSetters = {
    validFrom: setValidFrom,
    validNoticeFrom: setValidNoticeFrom,
    validThrough: setValidThrough,
  };

  const handleSave = () => {
    if (isSaveDisabled) return;

    onSave({
      validFrom: formatDateOnlyString(validFrom),
      validNoticeFrom: formatDateOnlyString(validNoticeFrom),
      validThrough: formatDateOnlyString(validThrough),
    });
  };

  return (
    <Modal
      open={open}
      onClose={isSaving ? undefined : onCancel}
      aria-labelledby="document-expiration-modal-title"
      sx={{ zIndex: 1400 }}
    >
      <Box sx={modalRootSx}>
        <Typography id="document-expiration-modal-title" sx={titleSx}>
          {LanguageConverterV2('userDetail.employeeDocumentation.expiration.title')}
        </Typography>
        <Box sx={fieldsSx}>
          {EXPIRATION_FIELDS.map((field) => {
            const errorKey = orderingErrorKeys[field.key];

            return (
              <Box key={field.key}>
                <Typography sx={fieldLabelSx}>{LanguageConverterV2(field.labelKey)}</Typography>
                <DatePickerField
                  fullWidth
                  value={fieldValues[field.key]}
                  onChange={fieldSetters[field.key]}
                  error={!!errorKey}
                  helperText={errorKey ? LanguageConverterV2(errorKey) : undefined}
                  testId={field.testId}
                  PopperProps={DATE_PICKER_OVERLAY_PROPS}
                  DialogProps={DATE_PICKER_OVERLAY_PROPS}
                />
              </Box>
            );
          })}
        </Box>
        <Box sx={footerSx}>
          <WaeButton
            type={BUTTON_TYPE.BUTTON}
            variant={BUTTON_VARIANT.DEFAULT}
            actionColor={BLACK[0]}
            hoverColor={BLACK[10]}
            disabled={isSaving}
            onClick={onCancel}
            sx={cancelButtonSx}
          >
            {LanguageConverterV2('button.cancel')}
          </WaeButton>
          <WaeButton
            type={BUTTON_TYPE.BUTTON}
            variant={BUTTON_VARIANT.DEFAULT}
            actionColor={PRIMARY_PURPLE[70]}
            hoverColor={PRIMARY_PURPLE[60]}
            disabled={isSaveDisabled}
            onClick={handleSave}
            sx={saveButtonSx}
          >
            {LanguageConverterV2('buttonText.save')}
          </WaeButton>
        </Box>
      </Box>
    </Modal>
  );
};

DocumentExpirationModal.propTypes = {
  document: PropTypes.shape({
    _id: PropTypes.string,
    validity: PropTypes.shape({
      range: PropTypes.shape({
        validFrom: PropTypes.string,
        validNoticeFrom: PropTypes.string,
        validThrough: PropTypes.string,
      }),
    }),
  }),
  isSaving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

DocumentExpirationModal.defaultProps = {
  document: null,
  isSaving: false,
};

export default DocumentExpirationModal;
